import { useState } from 'react';
import { toast } from 'sonner';
import { DatabaseBackup, Loader2, ShieldAlert, Users2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiGet, apiPost } from '@/lib/api';

const OFF = 'switched off in this deployment';

/** Read-only duplicate-account report + a verified users backup. Nothing here
 *  merges or deletes an account — that stays a human decision. */
export function AccountHygienePanel() {
  const [busy, setBusy] = useState('');
  const [backup, setBackup] = useState(null);
  const [snapshots, setSnapshots] = useState(null);
  const [report, setReport] = useState(null);
  const [disabled, setDisabled] = useState(false);

  const run = async (what, fn) => {
    setBusy(what);
    try {
      await fn();
    } catch (e) {
      const msg = e?.message || 'That did not work';
      if (msg.includes(OFF)) setDisabled(true);
      toast.error(msg.includes(OFF) ? 'These tools are switched off in this deployment' : msg);
    }
    setBusy('');
  };

  const takeBackup = () => run('backup', async () => {
    const res = await apiPost('/admin/users/backup', {});
    setBackup(res);
    setDisabled(false);
    toast.success(`Backed up ${res.documents_copied} accounts to ${res.snapshot}`);
  });

  const loadSnapshots = () => run('snapshots', async () => {
    const res = await apiGet('/admin/users/backups');
    setSnapshots(res.snapshots || []);
    setDisabled(false);
  });

  const loadDuplicates = () => run('duplicates', async () => {
    const res = await apiGet('/admin/users/duplicates');
    setReport(res);
    setDisabled(false);
    toast.success(res.duplicate_groups === 0
      ? 'No duplicate accounts — nothing to clean up'
      : `${res.duplicate_groups} duplicated address(es) found`);
  });

  return (
    <Card className="p-6 rounded-2xl" data-testid="account-hygiene-panel">
      <div className="flex items-start gap-3 mb-5">
        <Users2 className="w-5 h-5 mt-0.5 text-slate-500" />
        <div>
          <h3 className="font-heading text-lg">Account hygiene</h3>
          <p className="text-sm text-slate-500 font-body max-w-2xl">
            Find addresses held by more than one account and take a verified backup before
            anybody touches one. Emails are masked and <strong>nothing is ever merged or
            deleted here</strong> — the report only tells you which copy holds the data.
          </p>
        </div>
      </div>

      {disabled && (
        <div className="mb-5 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm font-body text-amber-900"
             data-testid="hygiene-disabled-notice">
          <ShieldAlert className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          These tools are deliberately off in production. Set <code>ADMIN_USER_TOOLS_ENABLED=true</code>
          {' '}in the Secrets tab, re-publish, use them, then set it back to <code>false</code> and re-publish.
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-5">
        <Button onClick={takeBackup} disabled={!!busy} data-testid="hygiene-backup-btn" className="rounded-xl">
          {busy === 'backup' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            : <DatabaseBackup className="w-4 h-4 mr-1.5" />}
          Back up all accounts
        </Button>
        <Button variant="outline" onClick={loadSnapshots} disabled={!!busy}
                data-testid="hygiene-snapshots-btn" className="rounded-xl">
          {busy === 'snapshots' && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
          List backups
        </Button>
        <Button variant="outline" onClick={loadDuplicates} disabled={!!busy}
                data-testid="hygiene-duplicates-btn" className="rounded-xl">
          {busy === 'duplicates' && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
          Find duplicate accounts
        </Button>
      </div>

      {backup && (
        <div className="mb-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-sm font-body"
             data-testid="hygiene-backup-result">
          <p className="font-semibold text-emerald-900">
            {backup.snapshot} · {backup.documents_copied} of {backup.documents_source} copied
            {backup.verified && <Badge className="ml-2 bg-emerald-600">verified</Badge>}
          </p>
          <p className="text-emerald-800 mt-1">{backup.restore_how_to}</p>
        </div>
      )}

      {snapshots && (
        <div className="mb-5 text-sm font-body" data-testid="hygiene-snapshot-list">
          {snapshots.length === 0 ? <p className="text-slate-500">No backups yet.</p> : snapshots.map(s => (
            <div key={s.snapshot} className="flex items-center justify-between py-1.5 border-b border-slate-100">
              <span className="font-mono text-xs">{s.snapshot}</span>
              <span className="text-slate-500">{s.documents} accounts
                {s.still_present ? '' : ' · missing'}</span>
            </div>
          ))}
        </div>
      )}

      {report && (
        <div data-testid="hygiene-duplicate-report">
          {report.duplicate_groups === 0 ? (
            <p className="text-sm font-body text-slate-600" data-testid="hygiene-no-duplicates">
              No address is held by more than one account. Nothing to approve.
            </p>
          ) : (
            <>
              <p className="text-sm font-body text-slate-600 mb-3">{report.note}</p>
              {report.groups.map(g => (
                <div key={g.email_masked} className="mb-4 p-4 rounded-xl border border-slate-200"
                     data-testid={`hygiene-group-${g.email_masked}`}>
                  <p className="font-heading text-sm mb-2">
                    {g.email_masked}
                    <Badge variant="outline" className="ml-2">{g.document_count} accounts</Badge>
                    {g.case_variants_present && <Badge variant="outline" className="ml-2">differs only by capitals</Badge>}
                  </p>
                  {g.documents.map(d => (
                    <div key={d.user_id}
                         className={`text-xs font-body py-1.5 px-2 rounded-lg mb-1 ${
                           d.user_id === g.likely_real_account ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                      <span className="font-mono">{d.user_id}</span>
                      {d.user_id === g.likely_real_account &&
                        <Badge className="ml-2 bg-emerald-600">holds the data</Badge>}
                      <span className="text-slate-500 ml-2">created {String(d.created_at).slice(0, 10)}</span>
                      <div className="text-slate-600 mt-1">
                        {Object.entries(d.signals || {}).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ')}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

export default AccountHygienePanel;
